// Recommended policy texts for Option Trading Wala (an option-chain analytics, screening and
// backtesting service sold as prepaid plans). They are shown on the public pages until the
// admin saves content, and can be loaded into the admin editor with "Use recommended text".
// These are drafts written for the platform's services, not legal advice — the business
// owner should review them (ideally with a lawyer) before relying on them.

const CONTACT_EMAIL = 'manishk004245@gmail.com';
const CONTACT_PHONE = '+91 9414004245';

export const POLICY_DEFAULTS = {
  privacyPolicy: {
    title: 'Privacy Policy',
    description: `
<p>This Privacy Policy explains how Option Trading Wala ("OTW", "we", "us") collects, uses, stores and protects your information when you use our website and services, including the live option chain, backtester, screeners, charts and related tools.</p>

<h2>1. Information we collect</h2>
<ul>
  <li><strong>Account details:</strong> your name, email address and mobile number provided at sign-up or login (including OTP verification).</li>
  <li><strong>Subscription details:</strong> the plan you choose, its validity and payment status. Card, UPI and bank details are entered on our payment partner's (Razorpay) secure page — we do not receive or store your full card or bank credentials.</li>
  <li><strong>Usage information:</strong> pages and tools you use, symbols you search, and basic technical data such as browser type, device and IP address, used to operate and secure the service.</li>
  <li><strong>Preferences stored on your device:</strong> settings such as theme, selected symbol and table options are saved in your browser's local storage so the site remembers them.</li>
</ul>

<h2>2. How we use your information</h2>
<ul>
  <li>To create and manage your account and give you access to the plan you purchased.</li>
  <li>To process payments, confirm subscriptions and send related notifications.</li>
  <li>To operate, maintain, troubleshoot and improve the platform and its features.</li>
  <li>To respond to your queries and provide customer support.</li>
  <li>To detect and prevent misuse, fraud or unauthorised access.</li>
</ul>
<p>We do not sell your personal information.</p>

<h2>3. Sharing of information</h2>
<p>We share information only as needed to run the service — for example with our payment partner to process payments, and with hosting and technical service providers who help us operate the platform. We may also disclose information where required by law or to protect the rights and safety of our users and the platform.</p>

<h2>4. Data storage and security</h2>
<p>Your information is stored on secured servers and access is restricted to authorised personnel. We use reasonable technical and organisational measures to protect it. However, no method of transmission or storage over the internet is completely secure, and we cannot guarantee absolute security.</p>

<h2>5. Data retention</h2>
<p>We keep your account and subscription information for as long as your account is active and as needed to provide the service, resolve disputes, and meet legal or accounting obligations.</p>

<h2>6. Your choices</h2>
<ul>
  <li>You can review and update your profile details from your account.</li>
  <li>You can ask us to correct or delete your account information by contacting us; some records may need to be retained where required by law.</li>
  <li>You can clear preferences stored on your device at any time using the "Clear Data / Cache" option or your browser settings.</li>
</ul>

<h2>7. Changes to this policy</h2>
<p>We may update this policy from time to time. The updated version will be posted on this page and applies from the date it is published.</p>

<h2>8. Contact us</h2>
<p>For any privacy questions or requests, contact us at <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a> or ${CONTACT_PHONE}.</p>
`.trim(),
  },

  termsAndCondition: {
    title: 'Terms & Conditions',
    description: `
<p>These Terms & Conditions govern your use of Option Trading Wala ("OTW", the "platform"). By creating an account or using the platform, you agree to these terms. If you do not agree, please do not use the platform.</p>

<h2>1. Nature of the service</h2>
<p>OTW provides market-data based analytics tools — including the live option chain, support / resistance levels, power stock screeners, charts and a historical backtester — for information and educational purposes. OTW does not execute trades, hold client funds, or manage investments on your behalf.</p>

<h2>2. Not investment advice</h2>
<p>Nothing on the platform is a recommendation, solicitation or offer to buy or sell any security, derivative or commodity. Levels, signals, labels (such as support, resistance, bullish or bearish) and analysis are generated from data using defined rules and are not personalised investment advice. You are solely responsible for your trading and investment decisions, and should consult a qualified, registered financial adviser where appropriate.</p>

<h2>3. Risk disclosure</h2>
<ul>
  <li>Trading in equities, futures, options and commodities involves substantial risk and may result in losses that exceed your initial investment.</li>
  <li>Past performance, backtest results and historical patterns do not guarantee future results.</li>
  <li>Markets can move rapidly; analysis may become outdated within seconds.</li>
</ul>

<h2>4. Market data</h2>
<p>Market data is obtained from third-party data providers and exchanges. While we aim for accuracy, data may be delayed, incomplete, interrupted or contain errors, and historical data may have gaps. Data is provided "as is" without any warranty of accuracy, completeness or timeliness. Always verify prices with your broker before trading.</p>

<h2>5. Accounts and responsibilities</h2>
<ul>
  <li>You must provide accurate information and keep your login credentials confidential.</li>
  <li>Your subscription is for your personal use. Sharing accounts, reselling access, or redistributing data or content from the platform is not permitted.</li>
  <li>You must not attempt to scrape, copy, reverse-engineer, overload or interfere with the platform.</li>
  <li>We may suspend or terminate accounts that violate these terms.</li>
</ul>

<h2>6. Subscriptions and payments</h2>
<p>Access to paid features requires an active plan. Plan prices, validity and inclusions are shown on the Pricing page and include applicable GST. Plans do not renew automatically. Cancellations and refunds are governed by our Cancellation & Refund Policy.</p>

<h2>7. Availability</h2>
<p>We aim to keep the platform available but do not guarantee uninterrupted service. Features may be changed, added or removed, and the platform may be unavailable during maintenance, data-provider outages or circumstances beyond our control.</p>

<h2>8. Intellectual property</h2>
<p>The platform, its design, software, tools and content belong to OTW or its licensors. You may not copy, modify or distribute them without written permission.</p>

<h2>9. Limitation of liability</h2>
<p>To the maximum extent permitted by law, OTW and its team are not liable for any trading losses, lost profits, or direct, indirect, incidental or consequential damages arising from your use of, or inability to use, the platform or its data. Where liability cannot be excluded, it is limited to the amount you paid for the subscription in question.</p>

<h2>10. Changes to these terms</h2>
<p>We may update these terms from time to time. Continued use of the platform after an update means you accept the revised terms.</p>

<h2>11. Contact</h2>
<p>Questions about these terms can be sent to <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a> or ${CONTACT_PHONE}.</p>
`.trim(),
  },

  canclePolicy: {
    title: 'Cancellation & Refund Policy',
    description: `
<p>This policy explains how cancellations and refunds work for Option Trading Wala ("OTW") subscription plans.</p>

<h2>1. Subscription plans</h2>
<ul>
  <li>Plans are prepaid for a fixed validity period (for example, monthly or yearly) as shown on the Pricing page. Prices include GST (18%).</li>
  <li>Plans <strong>do not renew automatically</strong>. Your access ends at the end of the validity period unless you purchase a new plan.</li>
  <li>Payments are processed securely through our payment partner, Razorpay.</li>
</ul>

<h2>2. Cancellation</h2>
<p>Because plans do not auto-renew, no action is needed to stop future charges — simply do not purchase a new plan. If you wish to stop using the service before your plan ends, your access will continue until the end of the paid validity period.</p>

<h2>3. Refund eligibility</h2>
<p>As the service gives immediate access to digital tools and data once a plan is activated, subscription fees are generally <strong>non-refundable</strong> after activation, including for partially used periods. Refunds may be considered in the following cases:</p>
<ul>
  <li><strong>Duplicate payment:</strong> you were charged more than once for the same plan.</li>
  <li><strong>Failed activation:</strong> payment was deducted but the plan was not activated on your account and we are unable to activate it.</li>
  <li><strong>Payment error:</strong> an incorrect amount was charged due to a technical error on our side.</li>
</ul>

<h2>4. Non-refundable charges</h2>
<ul>
  <li>Fees for a plan that has been activated and used, fully or partially.</li>
  <li>Charges where access was suspended due to a violation of our Terms & Conditions (for example, account sharing).</li>
  <li>Requests based on market outcomes, trading losses, or dissatisfaction with analysis results.</li>
</ul>

<h2>5. How to request a refund</h2>
<p>Email <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a> within 7 days of the payment with your registered email or mobile number, the plan name, the payment date and the Razorpay payment / transaction ID. We will review the request and reply by email.</p>

<h2>6. Refund processing</h2>
<p>Approved refunds are made to the original payment method through our payment partner. The time for the amount to reflect in your account depends on your bank or payment provider, typically 5–10 working days after approval.</p>

<h2>7. Changes to this policy</h2>
<p>We may update this policy from time to time. The version published on this page at the time of your purchase applies to that purchase.</p>
`.trim(),
  },
};

// Saved content counts only if it has real text (not empty or a placeholder from setup).
export const hasRealPolicyContent = (policy) => {
  const text = String(policy?.description || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  return text.length > 80;
};

// Field names used by the API for each policy: the current backend returns camelCase, the
// deployed one returns the database column names.
const POLICY_FIELDS = {
  privacyPolicy: ['privacyPolicy', 'privacy_policy'],
  termsAndCondition: ['termsAndCondition', 'terms_and_condition'],
  aboutUs: ['aboutUs', 'about_us'],
  canclePolicy: ['canclePolicy', 'cancelPolicy', 'cancel_policy'],
};

// One policy ({ title, description }) from the GET /content/policy record, whatever its naming.
export const readPolicy = (record, key) => {
  for (const name of POLICY_FIELDS[key] || [key]) {
    if (record?.[name]) return record[name];
  }
  return null;
};

// Save body: the edited policy plus every other stored policy, so a backend that writes all
// policy columns on each save keeps the ones not being edited.
export const buildPolicyUpdate = (record, key, value) => {
  const current = (k) => (k === key ? value : readPolicy(record, k));
  const cancel = current('canclePolicy');
  return {
    privacyPolicy: current('privacyPolicy'),
    termsAndCondition: current('termsAndCondition'),
    aboutUs: current('aboutUs'),
    cancelPolicy: cancel,
    canclePolicy: cancel,
  };
};
